import {
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Loader2,
    SquareArrowOutUpRight,
    SquareArrowUpRight,
    X,
    ZoomIn,
    ZoomOut,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { cfResizedUrl } from "@/lib/image-urls";
import { cn } from "@/lib/utils";

export interface ImageLightboxSlide {
    src: string;
    title?: string;
    /** Already-loaded thumbnail src used as instant placeholder */
    thumbSrc?: string;
}

interface ImageLightboxProps {
    open: boolean;
    index: number;
    slides: ImageLightboxSlide[];
    onClose: () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

export default function ImageLightbox({ open, index, slides, onClose }: ImageLightboxProps) {
    const [currentIndex, setCurrentIndex] = useState(index);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [loadedSrcs, setLoadedSrcs] = useState(new Set<string>());
    const dragStart = useRef({ x: 0, y: 0 });
    const panStart = useRef({ x: 0, y: 0 });
    const thumbnailsRef = useRef<HTMLDivElement>(null);

    // Sync index from parent when lightbox opens
    useEffect(() => {
        if (open) {
            setCurrentIndex(index);
            setZoom(1);
            setPan({ x: 0, y: 0 });
        }
    }, [open, index]);

    // Scroll active thumbnail into view
    useEffect(() => {
        if (!open) return;
        const container = thumbnailsRef.current;
        if (!container) return;
        const thumb = container.children[currentIndex] as HTMLElement | undefined;
        thumb?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, [currentIndex, open]);

    const resetZoom = useCallback(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, []);

    const goTo = useCallback(
        (i: number) => {
            if (i >= 0 && i < slides.length) {
                setCurrentIndex(i);
                resetZoom();
            }
        },
        [slides, resetZoom]
    );

    const zoomIn = useCallback(() => {
        setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
    }, []);

    const zoomOut = useCallback(() => {
        setZoom((z) => {
            const next = Math.max(z - ZOOM_STEP, MIN_ZOOM);
            if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
            return next;
        });
    }, []);

    // Keyboard navigation
    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            switch (e.key) {
                case "Escape":
                    e.preventDefault();
                    onClose();
                    break;
                case "ArrowLeft":
                    goTo(currentIndex - 1);
                    break;
                case "ArrowRight":
                    goTo(currentIndex + 1);
                    break;
                case "+":
                case "=":
                    zoomIn();
                    break;
                case "-":
                    zoomOut();
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown, true);
        return () => window.removeEventListener("keydown", handleKeyDown, true);
    }, [open, currentIndex, goTo, onClose, zoomIn, zoomOut]);

    // Trackpad/scroll wheel zoom
    const handleWheel = useCallback((e: WheelEvent) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const delta = -e.deltaY;
        setZoom((z) => {
            const next = Math.min(Math.max(z + delta * 0.01, MIN_ZOOM), MAX_ZOOM);
            if (next === MIN_ZOOM) setPan({ x: 0, y: 0 });
            return next;
        });
    }, []);

    useEffect(() => {
        if (!open) return;
        window.addEventListener("wheel", handleWheel, { passive: false });
        return () => window.removeEventListener("wheel", handleWheel);
    }, [open, handleWheel]);

    // Mouse drag for panning when zoomed
    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            if (zoom <= 1) return;
            setIsDragging(true);
            dragStart.current = { x: e.clientX, y: e.clientY };
            panStart.current = { ...pan };
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
        },
        [zoom, pan]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (!isDragging) return;
            setPan({
                x: panStart.current.x + (e.clientX - dragStart.current.x),
                y: panStart.current.y + (e.clientY - dragStart.current.y),
            });
        },
        [isDragging]
    );

    const handlePointerUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleImageLoad = useCallback((src: string) => {
        setLoadedSrcs((prev) => {
            const next = new Set(prev);
            next.add(src);
            return next;
        });
    }, []);

    if (!open) return null;

    const currentSlide = slides[currentIndex];
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < slides.length - 1;

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: lightbox overlay
        <div className="fixed inset-0 z-50 flex flex-col" onKeyDown={(e) => e.stopPropagation()}>
            {/* Backdrop */}
            <button
                type="button"
                className="absolute inset-0 bg-neutral-300/80 backdrop-blur-xs"
                onClick={onClose}
                tabIndex={-1}
                aria-label="Close lightbox"
            />

            {/* Toolbar */}
            <div className="relative z-10 flex h-14 items-center gap-2 bg-white px-3">
                {currentSlide?.title && (
                    <span className="mr-auto truncate font-medium text-orsane-primary">{currentSlide.title}</span>
                )}
                {!currentSlide?.title && <span className="mr-auto" />}

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={zoomIn}
                    disabled={zoom >= MAX_ZOOM}
                    className="text-orsane-muted hover:text-orsane-primary"
                >
                    <ZoomIn className="size-4.5" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={zoomOut}
                    disabled={zoom <= MIN_ZOOM}
                    className="text-orsane-muted hover:text-orsane-primary"
                >
                    <ZoomOut className="size-4.5" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                        if (currentSlide) window.open(currentSlide.src, "_blank");
                    }}
                    className="text-orsane-muted hover:text-orsane-primary"
                >
                    <SquareArrowOutUpRight className="size-4.5" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-orsane-muted hover:text-orsane-primary"
                >
                    <X className="size-4.5" />
                </Button>
            </div>

            {/* Main image area */}
            <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center overflow-hidden">
                {/* Navigation arrows */}
                {hasPrev && (
                    <Button
                        variant="ghost"
                        size="icon-lg"
                        className="absolute left-3 z-20"
                        onClick={() => goTo(currentIndex - 1)}
                    >
                        <ChevronLeft className="size-5" />
                    </Button>
                )}
                {hasNext && (
                    <Button
                        variant="ghost"
                        size="icon-lg"
                        className="absolute right-3 z-20"
                        onClick={() => goTo(currentIndex + 1)}
                    >
                        <ChevronRight className="size-5" />
                    </Button>
                )}

                {/* All slide images — rendered once, only current is visible */}
                {slides.map((slide, i) => {
                    const isCurrent = i === currentIndex;
                    const isLoaded = loadedSrcs.has(slide.src);
                    return (
                        <div
                            key={slide.src}
                            className={cn(
                                "absolute inset-0 flex size-full items-center justify-center px-16 py-4",
                                isCurrent
                                    ? cn(
                                          "z-10",
                                          zoom > 1 ? "cursor-grab" : "cursor-default",
                                          isDragging && "cursor-grabbing"
                                      )
                                    : "pointer-events-none invisible"
                            )}
                            onPointerDown={isCurrent ? handlePointerDown : undefined}
                            onPointerMove={isCurrent ? handlePointerMove : undefined}
                            onPointerUp={isCurrent ? handlePointerUp : undefined}
                        >
                            {/* Thumbnail placeholder */}
                            {isCurrent && !isLoaded && slide.thumbSrc && (
                                <img
                                    src={slide.thumbSrc}
                                    alt=""
                                    className="absolute inset-0 size-full select-none object-contain px-16 py-4"
                                    style={{
                                        transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                                    }}
                                    draggable={false}
                                />
                            )}
                            {/* Spinner fallback */}
                            {isCurrent && !isLoaded && !slide.thumbSrc && (
                                <Loader2 className="absolute size-8 animate-spin text-orsane-muted" />
                            )}
                            <img
                                src={cfResizedUrl(slide.src)}
                                alt={slide.title || ""}
                                className={cn(
                                    "relative max-h-full max-w-full select-none object-contain",
                                    isCurrent && isLoaded ? "opacity-100" : isCurrent ? "opacity-0" : ""
                                )}
                                style={
                                    isCurrent
                                        ? {
                                              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                                              transition: isDragging ? "none" : "transform 0.2s ease",
                                          }
                                        : undefined
                                }
                                draggable={false}
                                onLoad={() => handleImageLoad(slide.src)}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Thumbnails */}
            {slides.length > 1 && (
                <div className="relative z-10 flex justify-center bg-white/0 px-4 pb-4 pt-2">
                    <div ref={thumbnailsRef} className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                        {slides.map((slide, i) => (
                            <button
                                key={slide.src}
                                type="button"
                                onClick={() => goTo(i)}
                                className={cn(
                                    "shrink-0 overflow-hidden rounded-md border-2 bg-neutral-100 transition-colors",
                                    i === currentIndex
                                        ? "border-orsane-primary"
                                        : "border-neutral-300 hover:border-neutral-400"
                                )}
                                style={{ width: 80, height: 56 }}
                            >
                                <img
                                    src={slide.thumbSrc || slide.src}
                                    alt={slide.title || ""}
                                    className="size-full object-cover"
                                    draggable={false}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
